import React, { useState } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import Form, {
  useFormContext,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import { useQueryClient } from '@tanstack/react-query';
import { type RediscoveryApplication } from 'talent_matching/types';
import { useFetchModal } from 'candidate_profile_redesign/shared/form/useFetchModal';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { createBulkRediscoveryActions } from 'talent_matching/api';
import SelectJobStageFields from 'candidate_profile_redesign/modals/shared/select_job_stage_fields';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';

const t = withLocalePrefix(
  'talent_matching.rediscovery.bulk_actions.add_to_job'
);

type FormValues = {
  hiring_plan: Item;
  stage: Item;
};

type FooterProps = {
  onCancel: () => void;
  submitting: boolean;
};

const ModalFooter = ({ onCancel, submitting }: FooterProps) => {
  const {
    formState: { isValid },
  } = useFormContext();

  return (
    <DataEntryModal.Footer>
      <Stack direction="row" spacing={2}>
        <SecondaryButton onClick={onCancel} disabled={submitting}>
          {t('cancel')}
        </SecondaryButton>
        <PrimaryButton
          type="submit"
          disabled={submitting || !isValid}
          loading={submitting}
        >
          {t('submit')}
        </PrimaryButton>
      </Stack>
    </DataEntryModal.Footer>
  );
};

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  applicationIds: number[];
  totalSelected: number;
};

const BulkAddToJobModal = ({
  isOpen,
  closeModal,
  applicationIds,
  totalSelected,
}: Props) => {
  const queryClient = useQueryClient();
  const { hiringPlanId, hiringPlanName, userId, currentUserName } =
    useUserContext();
  const [submitting, setSubmitting] = useState(false);

  const firstApplication = queryClient.getQueryData<RediscoveryApplication>([
    'rediscovery-application',
    applicationIds[0],
  ]);
  const modalPath = firstApplication?.addPersonToJobModalPath ?? '';

  const { data } = useFetchModal<{ hiring_plans_path: string }>(
    modalPath,
    isOpen && !!modalPath
  );

  const onSubmit = async (formValues: FormValues) => {
    setSubmitting(true);
    try {
      await standardRequestWithErrorFlash.post(
        '/people/bulk/add_to_job',
        {
          hiring_plan_id: formValues.hiring_plan.value,
          initial_stage_id: formValues.stage.value,
          type: 'add',
          application_ids: applicationIds,
        },
        {
          successMessage: t('success', { count: totalSelected }),
        }
      );
      const actionType =
        formValues.hiring_plan.value === hiringPlanId.toString()
          ? 'added_to_this_job'
          : 'added_to_another_job';
      createBulkRediscoveryActions({
        action_type: actionType,
        application_ids: applicationIds,
        hiring_plan_id: hiringPlanId,
      });
      const now = new Date().toISOString();
      applicationIds.forEach((id, index) => {
        const cached = queryClient.getQueryData<RediscoveryApplication>([
          'rediscovery-application',
          id,
        ]);
        if (!cached) {
          return;
        }
        queryClient.setQueryData(['rediscovery-application', id], {
          ...cached,
          rediscoveryActions: [
            {
              id: -(index + 1),
              action: actionType,
              metadata: null,
              organization_id: 0,
              hiring_plan_id: hiringPlanId,
              application_id: id,
              user_id: userId,
              user_name: currentUserName,
              created_at: now,
              updated_at: now,
            },
            ...cached.rediscoveryActions,
          ],
        });
      });
      closeModal();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      onDismiss={closeModal}
      ariaLabel={t('title', { count: totalSelected })}
      size="medium"
    >
      <Form
        onSubmit={onSubmit}
        mode="onChange"
        defaultValues={{
          hiring_plan: {
            value: hiringPlanId.toString(),
            label: hiringPlanName,
          },
        }}
      >
        <DataEntryModal.Header onClick={closeModal}>
          {t('title', { count: totalSelected })}
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <Stack>
            <SelectJobStageFields hiringPlansPath={data?.hiring_plans_path} />
          </Stack>
        </DataEntryModal.Body>
        <ModalFooter onCancel={closeModal} submitting={submitting} />
      </Form>
    </DataEntryModal>
  );
};

export default BulkAddToJobModal;
